/** Extracts a readable message from an axios error (string or FastAPI 422 array `detail`). */

import { isAxiosError } from 'axios';

/**
 * One FastAPI/pydantic validation error item from a 422 `detail` array, e.g.
 * `{"type": "greater_than_equal", "loc": ["body", "context_window"],
 * "msg": "Input should be greater than or equal to 1", ...}`.
 */
interface ValidationErrorItem {
  loc?: unknown;
  msg?: unknown;
}

function formatValidationErrorItem(item: unknown): string {
  if (!item || typeof item !== 'object') return String(item);
  const { loc, msg } = item as ValidationErrorItem;
  // `loc` is ["body", ...path], and for this form's flat schema that's
  // always ["body", "<field name>"] -- the last non-"body" segment is the
  // field to name back to the admin.
  const path = Array.isArray(loc) ? loc.filter((segment) => segment !== 'body') : [];
  const field = path.length > 0 ? String(path[path.length - 1]) : null;
  const message = typeof msg === 'string' ? msg : 'Invalid value';
  return field ? `${field}: ${message}` : message;
}

// Deferred minor #10: this form is the first place in the app where a 422 is
// routinely reachable (the ge=1/le=2147483647 bounds on the context-window
// and max-output-tokens fields), and FastAPI/pydantic 422s carry `detail` as
// an ARRAY of validation-error objects, not a string -- unlike every 4xx
// this app's other admin panels normally see. Handling only the string case
// (the pattern this was cribbed from, under frontend/src/features/admin/)
// left an admin who submitted an out-of-range value with a bare "Request
// failed" and no field named.
export function extractErrorDetail(error: unknown): string {
  if (isAxiosError(error)) {
    const data: unknown = error.response?.data;
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail: unknown }).detail;
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail) && detail.length > 0) {
        return detail.map(formatValidationErrorItem).join('; ');
      }
    }
  }
  return 'Request failed. Please try again.';
}
